import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, MessageCircle, Heart, ClipboardList } from 'lucide-react';
import { useModeration } from '@/context/ModerationContext';
import { useDM } from '@/context/DMContext';
import { useAuth } from '@/context/AuthContext';
import { useFavorites } from '@/context/FavoritesContext';
import { useAudio } from '@/context/AudioContext';
import { usePanelQuery } from '@/hooks/useModalQuery';
import { DMInbox } from '@/features/chat/components/DMInbox';
import { FavoritesPanel } from '@/components/favorites';
import { PrayerApprovalQueue } from '@/components/PrayerApprovalQueue';
import { HeaderAudioIcon } from '@/components/HeaderAudioIcon';
import { AudioPillAnimation } from '@/components/AudioPillAnimation';
import { subscribeToPendingPrayersCount } from '@/services/prayers';
import { HeaderDonationBadge } from '@/components/DonationBadge';
import { useNewMembersTray } from '@/components/outreach/useNewMembersTray';

export function Header() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const { isModerationMode, toggleModerationMode, canModerate } = useModeration();
  const { totalUnreadCount, openConversation } = useDM();
  const { totalCount: favoritesCount } = useFavorites();
  const { animationTargetRef } = useAudio();

  // Favorites panel uses query params for browser history support
  const favoritesPanel = usePanelQuery('favorites');

  // Dropdowns use local state (ephemeral, not navigational)
  const [showInbox, setShowInbox] = useState(false);
  const [showPrayerQueue, setShowPrayerQueue] = useState(false);
  const [pendingPrayersCount, setPendingPrayersCount] = useState(0);

  // Track favorites pulse animation (pulses for 5 seconds when a new favorite is added)
  const [isFavoritesPulsing, setIsFavoritesPulsing] = useState(false);
  const prevFavoritesCount = useRef<number | null>(null);
  const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAuthenticated = authState.status === 'authenticated';
  const userProfile = authState.status === 'authenticated' ? authState.profile : null;

  // New Members: leaders and admins only. Shares the clipboard tray with prayers.
  const newMembers = useNewMembersTray({
    canModerate,
    profile: userProfile,
    trayOpen: showPrayerQueue,
    openTray: useCallback(() => setShowPrayerQueue(true), []),
    closeTray: useCallback(() => setShowPrayerQueue(false), []),
  });
  const trayCount = pendingPrayersCount + newMembers.waitingCount;

  // Subscribe to pending prayers count for moderators
  useEffect(() => {
    if (!canModerate) {
      setPendingPrayersCount(0);
      return;
    }

    const unsub = subscribeToPendingPrayersCount((count) => {
      setPendingPrayersCount(count);
    });

    return () => unsub();
  }, [canModerate]);

  // Pulse the favorites heart when a new favorite is added
  useEffect(() => {
    // Skip the initial render (when prevFavoritesCount is null)
    if (prevFavoritesCount.current === null) {
      prevFavoritesCount.current = favoritesCount;
      return;
    }

    // Trigger pulse when count increases (new favorite added)
    if (favoritesCount > prevFavoritesCount.current) {
      // Clear any existing timeout
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }

      setIsFavoritesPulsing(true);

      // Stop pulsing after 5 seconds
      pulseTimeoutRef.current = setTimeout(() => {
        setIsFavoritesPulsing(false);
      }, 5000);
    }

    prevFavoritesCount.current = favoritesCount;

    // Cleanup timeout on unmount
    return () => {
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, [favoritesCount]);

  const handleSelectConversation = (conversationId: string) => {
    // Open the specific conversation and navigate to messages tab
    openConversation(conversationId);
    navigate('/chat/messages');
    setShowInbox(false);
  };

  const handleViewAll = () => {
    navigate('/chat/messages');
    setShowInbox(false);
  };

  // Header gradient colors - 50% intensity of the main background gradient
  // Main bg uses: #fcf4fc -> #f8e8f8 -> #f0d2f0
  // Header uses softer versions that blend into white at top
  const headerGradient = 'linear-gradient(to top, #fdf8fd 0%, #fefcfe 40%, #ffffff 100%)';

  return (
    <header
      className="px-4 h-full flex items-center relative"
      style={{ background: headerGradient }}
    >
      {/* Smooth anti-aliased shadow drop - gradient-based for better blending */}
      <div
        className="absolute left-0 right-0 -bottom-3 h-3 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(240, 210, 240, 0.25) 0%, rgba(240, 210, 240, 0.08) 50%, transparent 100%)',
        }}
        aria-hidden="true"
      />
      <div className="max-w-md mx-auto w-full flex items-center justify-between gap-2">
        {/* Logo - aligned left */}
        <div className="shrink-0">
          <img
            src="/WISETemple_LogoCS5-WHITE-300x58.png"
            alt="Stephen Wise Temple"
            className="h-8 w-auto object-contain"
            style={{
              filter:
                'brightness(0) saturate(100%) invert(23%) sepia(15%) saturate(2000%) hue-rotate(270deg) brightness(95%) contrast(90%)',
            }}
          />
        </div>

        {/* Right side icons */}
        <div className="flex items-center gap-0.5 shrink-0">
          {/* Audio Player icon */}
          <HeaderAudioIcon ref={animationTargetRef} />

          {/* Donation badge (when position is header-inline) */}
          {isAuthenticated && (
            <HeaderDonationBadge
              onDonateClick={() => {
                // [FUTURE] Open donation modal when implemented
              }}
            />
          )}

          {/* Favorites icon */}
          {isAuthenticated && (
            <button
              onClick={favoritesPanel.open}
              className={`p-2 rounded-lg transition-all duration-200 group relative active:scale-90 ${
                favoritesPanel.isOpen
                  ? 'bg-red-50 text-red-500'
                  : 'text-slate-400 hover:text-red-500 hover:bg-red-50'
              }`}
              title="Favorites"
            >
              <Heart
                size={20}
                fill={favoritesCount > 0 ? 'currentColor' : 'none'}
                className={isFavoritesPulsing ? 'animate-pulse' : ''}
              />
            </button>
          )}

          {/* Prayer Approval Queue - Only visible to leaders/admins */}
          {canModerate && (
            <div className="relative">
              <button
                onClick={() => setShowPrayerQueue(!showPrayerQueue)}
                className={`p-2 rounded-lg transition-all duration-200 group relative active:scale-90 ${
                  showPrayerQueue
                    ? 'bg-amber-50 text-amber-600'
                    : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50'
                }`}
                title={newMembers.combined ? 'Needs a look' : 'Prayer Approval Queue'}
              >
                <ClipboardList size={20} />

                {/* Pending count badge */}
                {trayCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-amber-500 text-white text-[10px] font-bold rounded-full animate-pulse">
                    {trayCount > 9 ? '9+' : trayCount}
                  </span>
                )}
              </button>

              {/* Prayer Queue dropdown */}
              {showPrayerQueue && userProfile && (
                <PrayerApprovalQueue
                  onClose={() => setShowPrayerQueue(false)}
                  userId={userProfile.uid}
                  userName={userProfile.displayName}
                  newMembers={newMembers.newMembersProp}
                />
              )}
            </div>
          )}

          {/* Messages notification icon */}
          {isAuthenticated && (
            <div className="relative">
              <button
                onClick={() => setShowInbox(!showInbox)}
                className={`p-2 rounded-lg transition-all duration-200 group relative active:scale-90 ${
                  showInbox
                    ? 'bg-[#f8e8f8] text-[#8e4585]'
                    : 'text-slate-400 hover:text-[#8e4585] hover:bg-[#f8e8f8]'
                }`}
                title="Messages"
              >
                <MessageCircle size={20} />

                {/* Unread badge */}
                {totalUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-[#8e4585] text-white text-[10px] font-bold rounded-full">
                    {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                  </span>
                )}
              </button>

              {/* Inbox dropdown */}
              {showInbox && (
                <DMInbox
                  onClose={() => setShowInbox(false)}
                  onSelectConversation={handleSelectConversation}
                  onViewAll={handleViewAll}
                />
              )}
            </div>
          )}

          {/* Moderation Mode Toggle - Only visible to leaders/admins */}
          {canModerate && (
            <button
              onClick={toggleModerationMode}
              className={`p-2 rounded-lg transition-all duration-200 group relative active:scale-90 ${
                isModerationMode
                  ? 'bg-[#8e4585] text-white shadow-md'
                  : 'text-slate-400 hover:text-[#8e4585] hover:bg-[#f8e8f8]'
              }`}
              title={isModerationMode ? 'Moderation Mode: ON' : 'Moderation Mode: OFF'}
            >
              <ShieldCheck size={20} className={isModerationMode ? 'animate-pulse' : ''} />
              {/* Tooltip */}
              <span className="absolute right-0 top-full mt-2 px-2 py-1 text-xs bg-slate-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                {isModerationMode ? 'Moderation ON' : 'Moderation OFF'}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* New Members: say-hello sheet and dialogs (outlive the tray) */}
      {newMembers.flowElement}

      {/* Favorites Panel */}
      <FavoritesPanel isOpen={favoritesPanel.isOpen} onClose={favoritesPanel.close} />

      {/* Audio Pill Animation (rendered in portal) */}
      <AudioPillAnimation />
    </header>
  );
}
